import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Sparkles, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAiChat } from "@/components/ai/useAiChat";
import { ModelSelector } from "@/components/ai/ModelSelector";
import { ChatMessage } from "@/components/ai/ChatMessage";
import { SpeechSettings } from "@/components/ai/SpeechSettings";
import { useSpeechPlayer } from "@/components/ai/useSpeechPlayer";
import { useVoiceInput } from "@/components/ai/useVoiceInput";
import { VoiceInputPanel } from "@/components/ai/VoiceInputPanel";
import { ChatComposer } from "@/components/ai/ChatComposer";

const SUGGESTIONS = [
  "Suggest a gift box within ₹500",
  "I need snacks for 10 people. What would fit?",
  "When will preparation start after I order?",
  "I have a nut allergy. What should I check before ordering?",
  "How do I use the 7-millet powder for breakfast?",
];

export const openAiChat = (prompt) => window.dispatchEvent(new CustomEvent("kandula-ai-open", { detail: { prompt } }));

export default function AiChat() {
  const { items } = useCart();
  const cartIds = useMemo(() => items.map((i) => i.product.id), [items]);
  const { messages, send, streaming, reset, model, changeModel, ensureSession } = useAiChat(cartIds);
  const [open, setOpen] = useState(false);
  const speech = useSpeechPlayer(open);
  const voice = useVoiceInput(open, ensureSession);
  const cancelVoice = voice.cancel;
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  useEffect(() => { setInput(""); }, [voice.ownerKey]);

  useEffect(() => {
    const onOpen = (e) => {
      cancelVoice();
      setOpen(true);
      if (e.detail?.prompt) send(e.detail.prompt);
    };
    window.addEventListener("kandula-ai-open", onOpen);
    return () => window.removeEventListener("kandula-ai-open", onOpen);
  }, [send, cancelVoice]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open]);

  const submit = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming || voice.busy) return;
    voice.cancel();
    setInput("");
    send(text);
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.5 }}
        onClick={() => { speech.stop(); voice.cancel(); setOpen((o) => !o); }}
        data-testid="ai-chat-toggle"
        aria-label="Ask Kandula AI"
        className="fixed bottom-[88px] right-6 z-50 flex items-center gap-2 rounded-full bg-ragi-dark px-4 py-3 text-sm font-bold text-cream shadow-2xl shadow-ragi/30 transition-transform hover:scale-105"
      >
        <Sparkles className="h-4 w-4 text-saffron" /> Ask Kandula AI
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.section
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 bottom-3 z-[60] flex h-[min(80vh,620px)] flex-col overflow-hidden rounded-3xl border border-cream-muted bg-cream shadow-2xl shadow-ragi/20 sm:inset-x-auto sm:right-6 sm:w-[400px]"
            role="dialog"
            aria-label="Kandula AI assistant"
            data-testid="ai-chat-panel"
          >
            <header className="flex items-center justify-between bg-ragi-dark px-5 py-4 text-cream">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-saffron/20"><Sparkles className="h-4 w-4 text-saffron" /></span>
                <div>
                  <p className="font-display text-lg leading-tight" data-testid="ai-chat-title">Kandula AI</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/60" data-testid="ai-chat-subtitle">Products · Gifts · Order help</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { speech.stop(); voice.cancel(); setInput(""); reset(); }} className="rounded-full p-2 text-cream/60 hover:text-cream" aria-label="New conversation" data-testid="ai-chat-reset"><RotateCcw className="h-4 w-4" /></button>
                <button onClick={() => { speech.stop(); voice.cancel(); setOpen(false); }} className="rounded-full p-2 text-cream/60 hover:text-cream" aria-label="Close" data-testid="ai-chat-close"><X className="h-4 w-4" /></button>
              </div>
            </header>
            <ModelSelector value={model} onChange={changeModel} disabled={streaming || voice.busy} />
            <SpeechSettings speech={speech} />

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-lenis-prevent data-testid="ai-chat-messages">
              {messages.length === 0 && (
                <div data-testid="ai-chat-welcome">
                  <div className="rounded-2xl rounded-tl-sm bg-cream-secondary p-4 text-sm leading-relaxed text-ragi-dark">
                    Namaste! What are you looking for today? Tell me your budget, occasion or food preferences, and I’ll help you choose from our kitchen’s menu. Preparation timing is confirmed by the owner.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={s} onClick={() => { voice.cancel(); send(s); }} disabled={streaming || voice.busy} className="rounded-full border border-ragi/20 bg-cream px-3 py-1.5 text-left text-xs text-ragi-dark transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-50" data-testid={`ai-chat-suggestion-${i}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((message, index) => <ChatMessage key={message.id} message={message} index={index} speech={speech} disableSpeech={voice.busy} />)}
              <div ref={endRef} />
            </div>

            {voice.visible && <VoiceInputPanel voice={voice} draftLength={input.length} onRecord={() => { speech.stop(); voice.start(); }} onUse={(text) => { setInput((old) => old ? `${old} ${text}` : text); voice.cancel(); }} />}
            <ChatComposer input={input} setInput={setInput} onSubmit={submit} disabled={streaming || voice.busy || voice.loading} voiceOpen={voice.visible} onVoice={() => { speech.stop(); if (voice.visible) voice.cancel(); else voice.open(); }} />
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}
